import { useEffect } from "react";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";

/** After this much inactivity we stop the timer and finalize the view. */
const IDLE_TIMEOUT_MS = 60 * 1000;

/** Activity signals that count as the customer being engaged with the profile. */
const ACTIVITY_EVENTS = ["scroll", "click", "keydown", "mousemove", "touchstart"];

/**
 * Tracks how long a customer is actively engaged with a talent profile and sends
 * a single PROFILE_VIEW event carrying the accumulated active time.
 *
 * Why send once, at the end: the marketplace stores only the first PROFILE_VIEW
 * per customer/talent/day (a unique-daily index drops repeats), so the duration
 * has to ride inside that one insert. We therefore accumulate active time and
 * finalize on the first of: 60s of inactivity, tab hidden / page unload, or the
 * profile unmounting.
 *
 * "Active" time excludes idle gaps: after IDLE_TIMEOUT_MS without any activity
 * the timer pauses (and the view finalizes); it resumes on the next interaction.
 */
export default function useProfileViewTracking(talentId?: string): void {
  const { ingestAnalyticsEvent } = useTalentApi();
  useEffect(() => {
    if (!talentId) return undefined;

    let activeMs = 0;
    let lastTick = Date.now();
    let isActive = true;
    let sent = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const accumulate = () => {
      if (!isActive) return;
      const now = Date.now();
      activeMs += now - lastTick;
      lastTick = now;
    };

    const finalize = (keepalive: boolean) => {
      if (sent) return;
      sent = true;
      accumulate();
      if (idleTimer) clearTimeout(idleTimer);
      ingestAnalyticsEvent(
        {
          eventType: "PROFILE_VIEW",
          talentId,
          source: "PORTAL",
          activeDurationMs: Math.round(activeMs),
        },
        { keepalive }
      );
    };

    const goIdle = () => {
      accumulate();
      isActive = false;
      // User has gone quiet — stop the timer and persist the view.
      finalize(false);
    };

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(goIdle, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      if (sent) return;
      if (!isActive) {
        isActive = true;
        lastTick = Date.now();
      } else {
        accumulate();
      }
      resetIdleTimer();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") finalize(true);
    };

    const handlePageHide = () => finalize(true);

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);
    resetIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      // Component unmount (e.g. route change) — finalize the view.
      finalize(true);
    };
  }, [talentId, ingestAnalyticsEvent]);
}
